import type { Customer } from '../lib/api';

interface CustomerRailProps {
  customers: Customer[];
  selectedId: string;
  onSelect: (customer: Customer) => void;
}

/**
 * Left rail — the "Target" picker. Each row shows a brand-primary dot so the
 * palette a generated artifact will adopt is legible before you ever click
 * Generate. The selected row adopts the console accent (never the brand color —
 * brand colors belong to the artifact, not the chrome).
 */
export default function CustomerRail({ customers, selectedId, onSelect }: CustomerRailProps) {
  return (
    <nav aria-label="Target customer" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {customers.map((customer) => {
        const selected = customer.id === selectedId;
        return (
          <button
            key={customer.id}
            type="button"
            onClick={() => onSelect(customer)}
            aria-pressed={selected}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              width: '100%',
              textAlign: 'left',
              padding: '0.5rem 0.6rem',
              borderRadius: 10,
              border: '1px solid',
              borderColor: selected ? 'var(--accent)' : 'transparent',
              background: selected ? 'var(--accent-soft)' : 'transparent',
              color: selected ? 'var(--accent)' : 'var(--text)',
              transition: 'background 0.12s ease, border-color 0.12s ease',
            }}
          >
            <span
              aria-hidden
              style={{
                flex: '0 0 auto',
                width: 10,
                height: 10,
                borderRadius: 999,
                background: customer.brand.primaryColor,
                boxShadow: `0 0 0 3px ${customer.brand.primaryColor}22`,
              }}
            />
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: selected ? 600 : 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {customer.name}
              </span>
              {customer.industry && (
                <span
                  style={{
                    fontSize: 11,
                    color: selected ? 'var(--accent)' : 'var(--text-dim)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {customer.industry}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
